import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    User, Mail, Phone, Globe2, Calendar, BookOpen, ShieldAlert,
    CheckCircle2, Clock,
} from "lucide-react";
import { RadialProgress } from "@/components/ui/radial-progress";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return iso;
    }
}

/**
 * Instructor — read-only single-student dossier. Same shape as the admin
 * dossier (identity, enrolments with radial progress, recent quiz
 * attempts with integrity flags) but server-scoped to programs the
 * instructor owns. No destructive controls: instructors can't edit
 * email / residency / delete the account.
 */
export default function InstructorStudentDetail() {
    const { studentId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let live = true;
        api.get(`/instructor/students/${studentId}`)
            .then((r) => {
                if (live) setData(r.data);
            })
            .catch((err) => {
                if (live) {
                    setError(
                        err?.response?.data?.detail ||
                            "Could not load this student dossier.",
                    );
                }
            });
        return () => {
            live = false;
        };
    }, [studentId]);

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-12">
                <Link
                    to="/instructor/students"
                    className="text-sm text-muted-foreground hover:text-primary"
                >
                    ← My students
                </Link>
                <Card className="mt-6 border-rose-200">
                    <CardContent className="p-6">
                        <p className="font-medium text-rose-700">{String(error)}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="max-w-5xl mx-auto px-6 py-10 space-y-4">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    const u = data.user;
    const enrollments = data.enrollments || [];
    const attempts = data.attempts || [];
    const flaggedCount = attempts.filter(
        (a) => a?.integrity?.flagged,
    ).length;

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10 pb-24"
            data-testid="instructor-student-detail-page"
        >
            <Link
                to="/instructor/students"
                className="text-sm text-muted-foreground hover:text-primary"
            >
                ← My students
            </Link>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Identity */}
                <Card className="border-primary/15 lg:col-span-2">
                    <CardContent className="p-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-primary" />
                            <h2 className="font-display text-lg text-primary">
                                Identity
                            </h2>
                            <Badge
                                variant="outline"
                                className="ml-auto text-[10px] text-muted-foreground"
                            >
                                Read-only
                            </Badge>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                            <Field label="Full name" value={u.name} />
                            <Field
                                label="Student ID"
                                value={u.student_id}
                                mono
                            />
                            <Field
                                label="Email"
                                value={u.email}
                                Icon={Mail}
                            />
                            <Field
                                label="Phone"
                                value={u.phone}
                                Icon={Phone}
                            />
                            <Field
                                label="Residency"
                                value={u.residency}
                                Icon={Globe2}
                                capitalize
                            />
                            <Field
                                label="Joined"
                                value={fmtDate(u.created_at)}
                                Icon={Calendar}
                            />
                            <Field
                                label="Last seen"
                                value={fmtDate(u.last_seen_at)}
                                Icon={Clock}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Honour-code snapshot */}
                <Card className="border-primary/15">
                    <CardContent className="p-6 space-y-3">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="h-4 w-4 text-primary" />
                            <h2 className="font-display text-lg text-primary">
                                Honour-code signals
                            </h2>
                        </div>
                        <div className="font-display text-4xl text-foreground tabular-nums">
                            {flaggedCount}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Flagged quiz attempts across your programs out of{" "}
                            {attempts.length} total attempts.
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Enrolments with radial rings */}
            <Card className="mt-6 border-primary/15">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-primary" />
                        <h2 className="font-display text-lg text-primary">
                            Enrolments in your programs
                        </h2>
                    </div>
                    {enrollments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            This student isn't enrolled in any of your programs.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {enrollments.map((e) => (
                                <div
                                    key={e.enrollment_id}
                                    className="rounded-md border border-border bg-muted/20 px-3 py-2.5"
                                    data-testid={`instructor-enrollment-row-${e.enrollment_id}`}
                                >
                                    <div className="flex flex-col sm:flex-row items-start gap-4">
                                        <div className="shrink-0 self-center sm:self-start">
                                            <RadialProgress
                                                size={92}
                                                compact
                                                lessonsCompleted={e.lessons_completed || 0}
                                                totalLessons={e.total_lessons || 0}
                                                chaptersCompleted={e.chapters_completed || 0}
                                                totalChapters={e.total_chapters || 0}
                                                flagged={0}
                                                testId={`instructor-enrollment-ring-${e.enrollment_id}`}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0 w-full">
                                            <div className="flex items-center justify-between flex-wrap gap-2">
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold text-primary truncate">
                                                        {e.program_title}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        Enrolled {fmtDate(e.enrolled_at)}
                                                        {e.completed_at
                                                            ? ` · Completed ${fmtDate(e.completed_at)}`
                                                            : ""}
                                                    </div>
                                                </div>
                                                <Badge
                                                    variant="outline"
                                                    className="capitalize"
                                                >
                                                    {e.completed_at
                                                        ? "Completed"
                                                        : "In progress"}
                                                </Badge>
                                            </div>
                                            {(e.start_date || e.end_date) ? (
                                                <div className="text-[11px] text-muted-foreground mt-1">
                                                    Access:{" "}
                                                    {e.start_date ? fmtDate(e.start_date) : "open"}
                                                    {" → "}
                                                    {e.end_date ? fmtDate(e.end_date) : "open"}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Recent quiz attempts */}
            {attempts.length > 0 ? (
                <Card className="mt-6 border-primary/15">
                    <CardContent className="p-6 space-y-3">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                            <h2 className="font-display text-lg text-primary">
                                Recent quiz attempts ({attempts.length})
                            </h2>
                        </div>
                        <div className="divide-y divide-border/40">
                            {attempts.slice(0, 12).map((a) => (
                                <div
                                    key={a.id}
                                    className="py-2.5 flex items-center justify-between flex-wrap gap-2 text-sm"
                                >
                                    <div className="min-w-0">
                                        <div className="font-medium text-foreground truncate">
                                            {a.score}/{a.total} · {a.passed ? "Passed" : "Failed"}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground">
                                            {fmtDate(a.created_at)} · chapter {a.chapter_id?.slice(0, 8)}
                                        </div>
                                    </div>
                                    {a?.integrity?.flagged ? (
                                        <Badge className="bg-rose-100 text-rose-700 border-rose-200 gap-1">
                                            <ShieldAlert className="h-3 w-3" />
                                            Flagged · {a.integrity.total_violations || 0} signals
                                        </Badge>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            ) : null}
        </div>
    );
}

function Field({ label, value, Icon, mono, capitalize }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                {label}
            </div>
            <div
                className={`mt-0.5 text-foreground inline-flex items-center gap-1.5 ${
                    mono ? "font-mono" : ""
                } ${capitalize ? "capitalize" : ""}`}
            >
                {Icon ? <Icon className="h-3.5 w-3.5 text-muted-foreground" /> : null}
                {value || "—"}
            </div>
        </div>
    );
}
